/** How the order record's read-only (vendor / derived) fields render, and the value
 *  actions of its editable ones — one module, so a value that names another record offers
 *  the same links everywhere (orderLinks / bookingLinks).
 *
 *  - `orderFieldDisplay(key, c)` — the `display` of a vendor / derived key, from the
 *    order detail (catalog `detail_keys`). `null` renders the em dash.
 *  - `orderEditableDisplay(key, c)` — the view-mode rendering of an editable field whose
 *    stored value names records with their own links (the account, the order tags).
 *  - `orderFieldActions(key, c)` — the value actions of an editable field, from its LIVE
 *    value.
 *
 *  Times are UTC and say so (an order spans its bookings' tours; each booking page shows
 *  its tour's zone) — never the browser's zone. */
import { Box, Link } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { OrderRecordEditContext } from '@/api/orderRecord';
import type { OrderDetail } from '@/api/orders';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { phoneActions, refActions, restechAccountActions } from '@/features/bookings/overview/bookingLinks';
import { formatMoney } from '@/features/bookings/overview/common';
import { MONO_SX } from '@/features/bookings/record/panels/displays';
import { formatZoned } from '@/utils/zonedTime';

import {
  contactEmailActions,
  orderCustomIdActions,
  orderIntegrationActions,
  orderRefActions,
  orderTimestampActions,
  orderUserActions,
  type OrderCtx,
} from '../../overview/orderLinks';
import { ORDER_TIMEZONE } from '../views/spineModel';

export interface OrderDisplayContext {
  detail: OrderDetail;
  editContext: OrderRecordEditContext;
  links: OrderCtx;
}

function withActions(node: ReactNode, actions: ValueAction[]): ReactNode {
  return actions.length > 0 ? <ValueActions actions={actions}>{node}</ValueActions> : node;
}

function Mono({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={MONO_SX}>
      {children}
    </Box>
  );
}

const yesNo = (v: boolean | null | undefined) => (v === null || v === undefined ? null : v ? 'Yes' : 'No');

export function orderTime(iso: string | null | undefined): string | null {
  return iso ? formatZoned(iso, ORDER_TIMEZONE) : null;
}

function money(minor: number | null | undefined, d: OrderDetail): string | null {
  return formatMoney(minor ?? null, d.currency, d.currency_precision);
}

/** "Retail 387.00 GBP · Net 309.60 GBP" — only the amounts sent; Original only when it
 *  differs from Retail. */
function prices(d: OrderDetail): string | null {
  const parts = (
    [
      ['Retail', d.price_retail],
      ['Original', d.price_original !== d.price_retail ? d.price_original : null],
      ['Net', d.price_net],
    ] as const
  )
    .map(([label, v]) => {
      const m = money(v, d);
      return m ? `${label} ${m}` : null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** The read-only rendering of a vendor / derived field (`bookings` is the panel's). */
export function orderFieldDisplay(key: string, c: OrderDisplayContext): ReactNode | undefined {
  const { detail: d, links } = c;
  switch (key) {
    case 'start_datetime':
      return orderTime(d.start_datetime);
    case 'end_datetime':
      return orderTime(d.end_datetime);
    case 'currency':
      return d.currency || null;
    case 'prices':
      return prices(d);
    case 'tax_amount':
      return money(d.tax_amount, d);
    case 'settlement_method':
      return d.settlement_method || null;
    case 'email_receipt':
      return yesNo(d.email_receipt);
    case 'remote_created_ts':
      return orderTime(d.remote_created_ts);
    case 'remote_updated_ts':
      return orderTime(d.remote_updated_ts);
    case 'integration':
      return d.integration ? withActions(d.integration, orderIntegrationActions(d.integration, links)) : null;
    case 'restech_account':
      return d.restech_account
        ? withActions(<Mono>{d.restech_account}</Mono>, restechAccountActions(d.restech_account, links))
        : null;
    case 'created_ts':
      return d.created_ts ? withActions(orderTime(d.created_ts), orderTimestampActions('created', d.created_ts, links)) : null;
    case 'updated_ts':
      return d.updated_ts ? withActions(orderTime(d.updated_ts), orderTimestampActions('updated', d.updated_ts, links)) : null;
    default:
      return undefined;
  }
}

/** The view-mode rendering of an editable field whose stored value names records — the
 *  account (→ its user page) and the order's own tags, each chip with its actions. Only
 *  the stored value, never a draft's. */
export function orderEditableDisplay(key: string, c: OrderDisplayContext): ReactNode | undefined {
  const { detail: d, links } = c;
  const stored = c.editContext.baselines[`order:${c.editContext.order_id}`];
  if (key === 'user') {
    const u = d.user;
    if (!u || stored?.user !== u.id) return undefined;
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `#${u.id}`;
    return (
      <ValueActions actions={orderUserActions(u, links)}>
        <Link component={RouterLink} to={`/users/${u.id}`} underline="hover">
          {name}
        </Link>
      </ValueActions>
    );
  }
  if (key === 'tags') {
    const ids = stored?.tags;
    if (!Array.isArray(ids)) return undefined;
    const keep = new Set(ids);
    const tags = d.tags.filter((t) => keep.has(t.id));
    if (tags.length === 0) return null;
    return (
      <TagBadgeSet
        tags={tags}
        maxVisible={12}
        chipActions={(t) => tagChipActions(t, { tenantId: links.tenantId, owner: 'order' })}
      />
    );
  }
  return undefined;
}

/** Value actions of an editable field, built from its live value. */
export function orderFieldActions(key: string, c: OrderDisplayContext): ((value: unknown) => ValueAction[]) | undefined {
  const { detail: d, links } = c;
  const str = (fn: (v: string) => ValueAction[]) => (v: unknown) =>
    typeof v === 'string' && v.trim() ? fn(v.trim()) : [];
  switch (key) {
    case 'order_ref':
      return str((v) => orderRefActions(v, links));
    case 'custom_id':
      return str((v) => orderCustomIdActions(v, links));
    case 'supplier_reference':
      return str((v) => refActions('supplier', v, links));
    case 'reseller_ref':
      return str((v) => refActions('reseller', v, links));
    case 'contact_email':
      return str((v) => contactEmailActions(v, links));
    case 'contact_phone':
      return str((v) => phoneActions(v, links));
    case 'user':
      return (v) =>
        typeof v === 'number' ? orderUserActions({ id: v, email: d.user?.id === v ? d.user.email : null }, links) : [];
    default:
      return undefined;
  }
}
